import { FeatureFlag } from 'config';
import type ISku from './ISku';

interface IMenuItem {
  sku: ISku;
  quantity: number;
  sale_tag_enabled?: boolean;
  sale_price_per_serving?: number;

  readonly isDinner?: boolean;
  readonly isSnack?: boolean;
  readonly isBundle?: boolean;
  readonly isPreparedMeal?: boolean;
  readonly isMealKit?: boolean;
  readonly mealType?: string;
  readonly servingPrice?: number;
  readonly componentSkusQuantity?: number;
  readonly saleEnabled?: boolean;
}

class MenuItem<Sku extends ISku = ISku> implements IMenuItem {
  sku: Sku;
  quantity: number;
  price: string;
  premium: boolean;
  sale_enabled: boolean;
  sale_price_per_serving: number;

  constructor(attributes: IMenuItem) {
    Object.assign(this, attributes);
  }

  get isDinner(): boolean {
    return this.sku.meal_type === 'dinner';
  }

  get isOnSale(): boolean {
    return this.sale_enabled;
  }

  get isLunch(): boolean {
    return this.sku.meal_type === 'lunch';
  }

  get isBreakfast(): boolean {
    return this.sku.meal_type === 'breakfast';
  }

  get isBundle(): boolean {
    return this.sku.meal_type === 'bundle';
  }

  get isSnack(): boolean {
    return this.sku.meal_type === 'snack';
  }

  get isGroceryCategory(): boolean {
    return this.sku.meal_type === 'snack' || this.sku.meal_type === 'bundle';
  }

  get isPreparedMeal(): boolean {
    if (FeatureFlag.RTE_PRODUCT_SERVINGS) {
      return this.sku.product_line_name === 'Prepared';
    }
    return this.sku.plan_group === 'prepared_one_serving';
  }

  get isMealKit(): boolean {
    return this.sku.plan_group !== 'prepared_one_serving'
      && this.sku.meal_type !== 'snack' && this.sku.meal_type !== 'bundle';
  }

  get mealType(): string {
    return this.sku.plan_group === 'prepared_one_serving'
      ? 'prepared'
      : this.sku.meal_type;
  }

  get servingPrice(): number {
    return this.sku.subscription_price_per_serving;
  }

  get componentSkusQuantity(): number {
    return this.sku.ingredients_quantity;
  }

  get unitPrice(): number {
    const {
      subscription_price_per_serving,
      servings,
      sales_price,
    } = this.sku;

    const addOnPrice = sales_price || subscription_price_per_serving;

    return servings && servings > 1
      ? servings * subscription_price_per_serving
      : addOnPrice;
  }

  get totalPrice(): number {
    return this.unitPrice * this.quantity;
  }
}

export type { IMenuItem };
export default MenuItem;
